<script setup lang="ts">
  import { computed } from 'vue';
  import type { DocumentRevisionStatus } from '@/shared/lib/types/document-revision-status.types';
  import {
    formatDocumentRevisionStatus,
    getDocumentRevisionStatusDotClasses,
    getStatusDotClasses,
  } from '@/shared/lib/utils';
  import Tooltip from './Tooltip.vue';

  const props = defineProps<{
    status: DocumentRevisionStatus | string;
    label?: string;
    colorClasses?: string;
    focusable?: boolean;
  }>();

  const label = computed(() => props.label ?? formatDocumentRevisionStatus(props.status));

  // `colorClasses` carries only `bg-*`/`border-*`: the shape base is concatenated by the util, never
  // by the caller. A prop that replaced the whole string would make every caller reproduce that
  // base, and a caller that passed colors alone would render a dot with no size, radius or border —
  // while a test comparing two different color strings would still pass.
  const dotClasses = computed(() =>
    props.colorClasses
      ? getStatusDotClasses(props.colorClasses)
      : getDocumentRevisionStatusDotClasses(props.status)
  );

  // The accessible name goes wherever the focus goes. With `focusable` the tippy reference takes the
  // tab stop, and a focus stop with no name is announced as a bare generic; without it the name
  // stays on the dot, which is what the other consumers render.
  const referenceAria = computed(() =>
    props.focusable ? { role: 'img', 'aria-label': label.value } : {}
  );

  const dotAria = computed(() =>
    props.focusable ? {} : { role: 'img', 'aria-label': label.value }
  );
</script>

<template>
  <Tooltip
    :content="label"
    :focusable="focusable"
    v-bind="referenceAria"
  >
    <span
      :class="dotClasses"
      v-bind="dotAria"
    />
  </Tooltip>
</template>
